import { isImageEmpty, isImageReady } from '@/ui/editor/image-readiness'
import { campaignsShowQueryKey, mediaImageIndexQueryKey, useCampaignsShow, useStoriesUpdate } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import {
	activeStoryChatToolAtom,
	ConformedCampaignStoryAd,
	conformStoryAd,
	usePrevious,
	useStoryEditor,
} from '../../ui'

export function useNewStoryEditor(campaignId: string) {
	const campaign = useCampaignsShow(campaignId)
	const queryClient = useQueryClient()

	const story = campaign.data?.story

	const storyAd: ConformedCampaignStoryAd | undefined = story && story.id
		? conformStoryAd({
			id: story.id,
			title: story.title ?? '',
			subtitle: story.subtitle ?? '',
			location: story.location ?? '',
			ctaText: story.button ?? '',
			imageId: story.imageId ?? '',
			meta: story.meta?.__ui ?? {},
		})
		: undefined

	const imagesHistory = story?.imagesHistory?.filter((image) => !isImageEmpty(image)).map((image) => ({
		id: image.id,
		category: 'image' as const,
		collection: image.collectionName,
		name: image.name,
		fileName: image.fileName,
		humanReadableSize: image.humanReadableSize,
		createdAt: new Date(image.createdAt),
		url: image.url,
		width: image.width,
		height: image.height,
		isReady: isImageReady(image),
	}))

	const image = story?.image
		? {
			id: story.image.id,
			url: story.image.url,
			isReady: isImageReady(story.image),
			isEmpty: isImageEmpty(story.image),
		}
		: undefined

	const storyMutation = useStoriesUpdate({
		mutationKey: ['update-story'],
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
	})

	const handleStoryAdChange = async (value: ConformedCampaignStoryAd) => {
		if (!story) {
			throw new Error('Story not found')
		}
		if (!story.id) {
			throw new Error('Story ID missing')
		}

		await storyMutation.mutateAsync({
			story: story.id,
			data: {
				...story,
				title: value.title,
				subtitle: value.subtitle,
				location: value.location,
				button: value.ctaText,
				imageId: value.imageId,
				meta: {
					...(story.meta || {}),
					__ui: value.meta,
				},
			},
		})
	}

	const isToolExecuting = useAtomValue(activeStoryChatToolAtom)

	const { lifecycle } = useStoryEditor({
		storyAd,
		isPending: campaign.isPending,
		isGenerating: story?.isGenerated !== true,
		isToolExecuting,
		imagesHistory,
		image,
		onStoryAdChange: handleStoryAdChange,
		storyAdVersion: story?.updatedAt,
	})

	// Invalidate media queries when transitioning to 'ready' state
	const prevLifecycle = usePrevious(lifecycle)
	useEffect(() => {
		if (lifecycle === 'ready' && prevLifecycle !== 'ready') {
			void queryClient.invalidateQueries({
				queryKey: mediaImageIndexQueryKey(),
			})
		}
	}, [lifecycle, prevLifecycle, queryClient])

	return {
		isSaving: storyMutation.isPending,
		isGenerating: story?.isGenerated !== true,
	}
}
